import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_ENDPOINTS, buildApiUrl } from '../../config/apiConfig';
import AdminHubPageFrame from '../../components/admin/AdminHubPageFrame';
import AdminSectionHeader from '../../components/admin/AdminSectionHeader';
import AdminDataTable from '../../components/admin/AdminDataTable';
import AdminJsonDetails from '../../components/admin/AdminJsonDetails';

export default function AdminPlatformFlagsPage() {
  const { t } = useLanguage();
  const [descriptor, setDescriptor] = useState(null);
  const [billing, setBilling] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [dRes, bRes] = await Promise.all([
          fetch(buildApiUrl(API_ENDPOINTS.ADMIN_SETTINGS_COMMERCIAL_DESCRIPTOR), { credentials: 'include' }),
          fetch(buildApiUrl(API_ENDPOINTS.ADMIN_BILLING_SETTINGS), { credentials: 'include' }),
        ]);
        if (!dRes.ok || !bRes.ok) throw new Error('bad');
        const dJson = await dRes.json();
        const bJson = await bRes.json();
        if (!dJson.descriptor || !Array.isArray(dJson.descriptor.entries)) throw new Error('bad');
        if (!cancel) {
          setDescriptor(dJson.descriptor);
          setBilling(bJson);
          setErr(false);
        }
      } catch {
        if (!cancel) setErr(true);
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const rows = useMemo(() => {
    const entries = Array.isArray(descriptor?.entries) ? descriptor.entries : [];
    return entries.map((e, i) => ({
      __rowKey: `${e.key}-${i}`,
      key: e.key,
      label: e.descriptor?.label || e.key,
      scope: e.descriptor?.scope || '—',
      readiness: e.descriptor?.enforcementReadiness || '—',
      value: e.value === undefined || e.value === null ? '—' : typeof e.value === 'object' ? JSON.stringify(e.value) : String(e.value),
    }));
  }, [descriptor]);

  const cols = useMemo(
    () => [
      { key: 'key', header: t('adminKit.colFlagKey') },
      { key: 'label', header: t('adminKit.colLabel') },
      { key: 'scope', header: t('adminKit.colScope') },
      { key: 'readiness', header: t('adminKit.colReadiness') },
      { key: 'value', header: t('adminKit.colValue') },
    ],
    [t]
  );

  const crumbs = useMemo(
    () => [
      { label: t('adminHub.navPlatform'), to: '/admin/platform/feature-flags' },
      { label: t('adminHub.navFeatureFlags') },
    ],
    [t]
  );

  const payload = { descriptor, billingSettings: billing };

  return (
    <AdminHubPageFrame titleKey="adminHub.flagsTitle" descKey="adminHub.flagsDesc" breadcrumbItems={crumbs}>
      <section className="admin-section-card">
        {err ? (
          <div className="admin-state admin-state--error" role="status">
            <p>{t('adminHub.loadError')}</p>
          </div>
        ) : !descriptor ? (
          <div className="admin-state admin-state--loading" role="status">
            <p>{t('adminPage.loading')}</p>
          </div>
        ) : (
          <div className="admin-hub-kit-stack">
            <AdminSectionHeader title={t('adminKit.flagsDescriptorTitle')} subtitle={t('adminKit.flagsDescriptorSub')} />
            <AdminDataTable columns={cols} rows={rows} emptyMessage={t('adminKit.tableEmpty')} />
            <AdminJsonDetails title={t('adminKit.rawJsonToggle')} data={payload} />
          </div>
        )}
      </section>
    </AdminHubPageFrame>
  );
}
